import { Link } from "react-router-dom";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import { deepWithStats } from "@/data/modelStats";
import { useTranslation } from "react-i18next";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  // `lng` present on /:lng/blog, absent on the French root /blog. Keeps post
  // links within the active locale so /es/blog cards go to /es/blog/{slug}.
  const localePrefix = useLocalePrefix();
  const rawPosts = deepWithStats(t("blog.posts", { returnObjects: true }));
  const posts = Array.isArray(rawPosts) ? rawPosts : [];

  // Curated posts store a pre-formatted date label ("Mars 2026"); the daily
  // auto-generated posts (generate_blog_post.py) store a raw ISO date
  // ("2026-05-17"). Only reformat the ISO ones, localized, so every card reads
  // consistently instead of mixing "Mars 2026" with "2026-05-17".
  const formatDate = (d) => {
    if (typeof d !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    try {
      return new Date(d + "T00:00:00").toLocaleDateString(
        i18n.language || "fr",
        { year: "numeric", month: "long", day: "numeric" }
      );
    } catch {
      return d;
    }
  };

  return (
    <PageLayout>
      <SEO
        title={t("blog.index.title")}
        description={t("blog.index.meta")}
        canonical="/blog"
      />
      <section className="section-y-sm px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-primary text-sm font-medium mb-6">
              {t("blog.index.badge")}
            </span>
            <h1 className="animate-in-up font-heading text-fluid-h1 font-bold text-white mb-4">
              {t("blog.index.title")}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {t("blog.index.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <Link
                key={i}
                to={`${localePrefix}/blog/${post.slug}`}
                className="glass rounded-2xl overflow-hidden hover:border-primary/30 transition-all group block"
              >
                <div className="h-2 bg-gradient-to-r from-primary to-accent" />
                <div className="p-6">
                  <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                    {post.category || t("blog.defaultCategory", "Article")}
                  </span>
                  <h2 className="font-heading text-lg font-semibold text-white mt-3 mb-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-slate-400 text-xs">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
export default BlogPage;
